import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatCurrency } from "@/lib/currency";
import {
  ArrowDownCircle, ArrowUpCircle, ArrowLeftRight,
  BookOpen, Users, TrendingDown,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function PaymentHub() {
  const { data: unpaidCustomers = [] } = useQuery<{ totalBalance: number }[]>({
    queryKey: ["customers-unpaid"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/customers/unpaid`, { credentials: "include" });
      return res.ok ? res.json() : [];
    },
  });

  const { data: supplierLedger = [] } = useQuery<{ totalOwed: number }[]>({
    queryKey: ["supplier-ledger"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/purchases/supplier-ledger`, { credentials: "include" });
      return res.ok ? res.json() : [];
    },
  });

  const { data: transfers = [] } = useQuery<{ id: number }[]>({
    queryKey: ["fund-transfers"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/fund-transfers`, { credentials: "include" });
      return res.ok ? res.json() : [];
    },
  });

  const totalCustomerOwed = unpaidCustomers.reduce((s, c) => s + c.totalBalance, 0);
  const totalSupplierOwed = supplierLedger.reduce((s, c) => s + c.totalOwed, 0);

  const cards = [
    {
      group: "Customer & Sales (Inbound)",
      items: [
        {
          href: "/unpaid",
          icon: ArrowDownCircle,
          color: "bg-emerald-50 text-emerald-600",
          ring: "hover:ring-emerald-200",
          title: "Payment In",
          desc: "Receive payments from customers and settle credit sales.",
          badge: unpaidCustomers.length > 0 ? `${unpaidCustomers.length} customer${unpaidCustomers.length > 1 ? "s" : ""} owe ${formatCurrency(totalCustomerOwed)}` : null,
          badgeColor: "bg-amber-100 text-amber-800",
        },
        {
          href: "/customers",
          icon: Users,
          color: "bg-blue-50 text-blue-600",
          ring: "hover:ring-blue-200",
          title: "Customer Ledger",
          desc: "View all customers and their account balances.",
          badge: null,
          badgeColor: "",
        },
      ],
    },
    {
      group: "Supplier & Purchases (Outbound)",
      items: [
        {
          href: "/supplier-ledger",
          icon: ArrowUpCircle,
          color: "bg-red-50 text-red-600",
          ring: "hover:ring-red-200",
          title: "Payment Out",
          desc: "Pay suppliers and settle outstanding purchase invoices.",
          badge: supplierLedger.length > 0 ? `You owe ${formatCurrency(totalSupplierOwed)} to ${supplierLedger.length} supplier${supplierLedger.length > 1 ? "s" : ""}` : null,
          badgeColor: "bg-red-100 text-red-800",
        },
        {
          href: "/supplier-ledger",
          icon: BookOpen,
          color: "bg-orange-50 text-orange-600",
          ring: "hover:ring-orange-200",
          title: "Supplier Ledger",
          desc: "View supplier accounts and outstanding balances.",
          badge: null,
          badgeColor: "",
        },
      ],
    },
    {
      group: "Internal Accounts",
      items: [
        {
          href: "/fund-transfers",
          icon: ArrowLeftRight,
          color: "bg-yellow-50 text-yellow-600",
          ring: "hover:ring-yellow-200",
          title: "Fund Transfers",
          desc: "Move money between cash, bank, POS, and mobile money accounts.",
          badge: transfers.length > 0 ? `${transfers.length} transfer${transfers.length > 1 ? "s" : ""} recorded` : null,
          badgeColor: "bg-blue-100 text-blue-800",
        },
        {
          href: "/accounting/payments",
          icon: TrendingDown,
          color: "bg-purple-50 text-purple-600",
          ring: "hover:ring-purple-200",
          title: "Payment History",
          desc: "View all payment transactions recorded in the system.",
          badge: null,
          badgeColor: "",
        },
      ],
    },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Payment Hub</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage all money movement — inbound, outbound, and internal transfers.
        </p>
      </div>

      {cards.map(group => (
        <div key={group.group}>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            {group.group}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {group.items.map(item => (
              <Link key={item.href + item.title} href={item.href}>
                <div className={`bg-card border rounded-xl p-5 flex gap-4 items-start cursor-pointer hover:shadow-md transition-all hover:ring-2 ${item.ring}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-base">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{item.desc}</p>
                    {item.badge && (
                      <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
